import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContrastIcon from '@mui/icons-material/Contrast';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import LinearProgress, { LinearProgressProps } from '@mui/material/LinearProgress';
import makeStyles from '@mui/styles/makeStyles';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import * as React from 'react';
import useSWR from 'swr';

import { generateBill } from '@/Billing/api';
import { SWLoadingButton } from '@/components/LoadingButton';
import { useModal } from '@/components/ModalLoading/context';
import { OrderIcon } from '@/components/OrderIcon';
import useMixPanel from '@/hooks/useMixPanel';
import { defaultOutageStatusResponse, getOutageStatus } from '@/Outage/api';
import {
  mapOutageStatusToIsElasticsearchOutage,
  OUTAGE_STATUS_REFRESH_MILLIS,
  OUTAGE_STATUS_SWR_KEY,
} from '@/Outage/util';
import { Unit } from '@/Units/types';

import { Breakdown as BillBreakdown } from '../../Billing/BreakdownDialog';
import { getDueDate, formatDueDate } from '../../Billing/dueDateUtils';
import { FormDialog as BillForm, FormValues } from '../../Billing/FormDialog';
import type { Bill, IInvoices, ILineItems } from '../../Billing/types';
import { useBills } from '../../Billing/useBills';
import { DeleteConfirmDialog } from '../../components/Dialog';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { Address, RateMethod } from '../../types';
import { convertTimestampToTimeZone, formatNumber } from '../../util';
import { StructureBilling } from '../types';

dayjs.extend(utc);
dayjs.extend(timezone);

function LinearProgressWithLabel(props: LinearProgressProps & { value: number; label: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '100%', mr: 1 }}>
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography fontSize={10} sx={{ color: 'text.secondary' }}>
          {props.label}
        </Typography>
      </Box>
    </Box>
  );
}

type SortDirection = 'asc' | 'desc';

type SortOrder = -1 | 0 | 1;

type SortableProperty = 'name' | 'start' | 'end' | 'volume' | 'cost' | 'due' | 'status';

type RowData = {
  id: string;
  name: string;
  start: number;
  end: number;
  volume: number;
  cost: number;
  due: number | null;
  status: number;
  rateMethod?: RateMethod;
  effectiveRate?: number | null;
  volumeUnits: string;
  billsSentTimestamp: number | null | undefined;
  invoices: IInvoices[];
  lineItems: ILineItems[];
};

type Comparator = (a: RowData, b: RowData, orderBy?: SortableProperty) => SortOrder;

// Util ///////////////////////////////////////////////////////////////////////

/**
 * Sort in descending order
 *
 * @param {object} a - First object to compare
 * @param {object} b - Second object to compare
 * @param {string} orderBy - Property name to order by
 *
 * @returns {-1 | 0 | 1} - Sort order
 */
const descendingComparator = (a: RowData, b: RowData, orderBy: SortableProperty) => {
  const aVal = a[orderBy];
  const bVal = b[orderBy];

  // Handle null values - put them at the end regardless of sort direction
  if (aVal === null && bVal === null) return 0;
  if (aVal === null) return 1; // a goes after b
  if (bVal === null) return -1; // b goes after a

  if (bVal < aVal) {
    return -1;
  }
  if (bVal > aVal) {
    return 1;
  }
  return 0;
};

/**
 * Get sorting function
 *
 * @param {'asc' | 'desc'} order - Direction to sort
 * @param {*} orderBy - Property name to order by
 *
 * @returns {Function} - Function to sort with
 */
const getComparator = (order: SortDirection, orderBy: SortableProperty): Comparator => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy) as SortOrder
    : (a, b) => -descendingComparator(a, b, orderBy) as SortOrder;
};

/**
 * Sort row data
 *
 * @param {RowData[]} array - Array of RowData
 * @param {Function} comparator - Comparing/sorting function
 *
 * @returns {RowData[]} - Sorted RowData
 */

const hiddenArrow = () => <span />;
const stableSort = (array: RowData[], comparator: Comparator): RowData[] => {
  const stabilizedThis = array.map((el, index) => [el, index]);

  stabilizedThis.sort((a, b) => {
    // $FlowFixMe
    const order = comparator(a[0] as RowData, b[0] as RowData);

    if (order !== 0) return order;

    return (a as number[])[1] - (b as number[])[1];
  });

  return stabilizedThis.map((el) => el[0] as RowData);
};

/**
 * Convert Bills to table rows
 *
 * @param  {object[]} billArr - Array of Bills
 * @param  {number} dueDateDays - Days until due (default 21)
 * @param  {boolean} stripeConnect - Whether this is a Stripe-connected account
 * @param  {string} timeZone - Property timezone for date calculations
 * @returns {object[]} - Array of rows
 */
const billsToRows = (
  billArr: Bill[],
  dueDateDays: number = 21,
  stripeConnect: boolean = false,
  timeZone?: string,
): RowData[] => {
  return billArr.map((b) => {
    // Get actual due date from invoice if available, otherwise calculate from billsSentTimestamp
    const invoiceWithDueDate = b.invoices.find((inv) => inv.customInvoiceDueDate);
    const dueTimestamp = getDueDate(
      invoiceWithDueDate?.customInvoiceDueDate,
      b.billsSentTimestamp,
      dueDateDays,
      timeZone,
    );

    // Calculate status percentage for sorting
    let statusPercent = 0;
    if (b.invoices.length > 0) {
      if (stripeConnect) {
        // Stripe: percentage of paid invoices
        const paidCount = b.invoices.filter((inv) => inv.stripeLastStatus === 'paid').length;
        statusPercent = (paidCount / b.invoices.length) * 100;
      } else {
        // Non-Stripe: percentage of generated invoices
        const generatedCount = b.invoices.filter((inv) => inv.customInvoicePdfUrl).length;
        statusPercent = (generatedCount / b.invoices.length) * 100;
      }
    }

    return {
      id: b.id,
      name: b.name || '',
      start: b.startDate,
      end: b.endDate,
      cost: b.cost,
      volume: b.volume,
      due: dueTimestamp,
      status: statusPercent,
      rateMethod: b.rateMethod,
      volumeUnits: b.volumeUnits,
      effectiveRate: b.effectiveRate,
      billsSentTimestamp: b.billsSentTimestamp,
      invoices: b.invoices,
      lineItems: b.lineItems,
    };
  });
};

// Components /////////////////////////////////////////////////////////////////

// Head ///////////////////////////////

let headCells = [
  { id: 'start', numeric: false, disablePadding: false, label: 'Start Date' },
  { id: 'end', numeric: false, disablePadding: false, label: 'End Date' },
  { id: 'volume', numeric: false, disablePadding: false, label: 'Water Usage' },
  { id: 'unit', numeric: false, disablePadding: false, label: 'Volume Unit' },
  // { id: 'effectiveRate', numeric: false, disablePadding: false, label: 'Effective Rate' },
  { id: 'cost', numeric: false, disablePadding: false, label: 'Amount' },
  { id: 'due', numeric: false, disablePadding: false, label: 'Due Date' },
  { id: 'status', numeric: false, disablePadding: false, label: 'Invoice Status' },
];

type HeadProps = {
  classes: { [x: string]: string };
  order: SortDirection;
  orderBy: SortableProperty;
  onRequestSort: (event: React.MouseEvent, property: SortableProperty) => void;
  stripeConnect: boolean;
};

const EnhancedTableHead = ({
  classes: _classes, // destructuring alias to avoid unused variable warning
  order,
  orderBy,
  onRequestSort,
  stripeConnect,
}: HeadProps) => {
  const createSortHandler = (property: SortableProperty) => (event: React.MouseEvent) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => {
          return (
            <TableCell variant="head" key={headCell.id} align="center" sortDirection={false}>
              <TableSortLabel
                IconComponent={hiddenArrow}
                onClick={createSortHandler(headCell.id as SortableProperty)}
              >
                {headCell.label}
                {orderBy === headCell.id && (
                  <span
                    style={{
                      position: 'absolute',
                      right: '-25px',
                    }}
                  >
                    <OrderIcon type={order} />
                  </span>
                )}
              </TableSortLabel>
            </TableCell>
          );
        })}
        <TableCell align={'justify'}></TableCell>
      </TableRow>
    </TableHead>
  );
};

const EnhancedTableToolbar = ({ onAdd }) => {
  return (
    <Box margin={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" spacing={0}>
        <Typography variant="h5" id="tableTitle" component="div" fontWeight={600}>
          Water Utility Bill
        </Typography>
        <SWLoadingButton onClick={onAdd} sx={{ ml: 2 }}>
          Add Bill
        </SWLoadingButton>
      </Stack>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: `${theme.spacing(4)} 0 ${theme.spacing(3)}`,
  },
  paper: {
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 650,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}));

type Props = {
  bills: Bill[];
  structureId: string;
  structureName: string;
  isLoading: boolean;
  defaultRateMethod: RateMethod;
  structureAddress: Address;
  timeZone: string;
  stripeConnect: boolean;
  units: Unit[];
  volumeUnits: string;
  payOnlineUrl?: string | null;
  paymentInstructions?: string | null;
  dueDateDays?: number | null;
  onPaymentSettingsUpdate?: (
    payOnlineUrl: string | null,
    paymentInstructions: string | null,
    dueDateDays: number | null,
  ) => Promise<void>;
};

export const Billing = ({
  bills,
  structureId,
  structureName,
  isLoading,
  defaultRateMethod,
  structureAddress,
  timeZone,
  stripeConnect,
  units,
  volumeUnits,
  payOnlineUrl,
  paymentInstructions,
  dueDateDays,
  onPaymentSettingsUpdate,
}: Props): JSX.Element => {
  const [order, setOrder] = React.useState<SortDirection>('desc');
  const [orderBy, setOrderBy] = React.useState<SortableProperty>('start');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [isAddBillOpen, setIsAddBillOpen] = React.useState(false);
  const [isEditingBill, setIsEditingBill] = React.useState<string | null>(null);
  const [selectedBillingDetail, setSelectedBillingDetail] = React.useState<StructureBilling | null>(
    null,
  );
  const [openConfirmModal, setOpenConfirmModal] = React.useState(false);
  const [selectedRowId, setSelectedRowId] = React.useState<string | null>(null);
  const [selectedRow, setSelectedRow] = React.useState<RowData>();
  const [loading, setLoading] = React.useState(false);
  const { create, edit, remove, mutate } = useBills(structureId);
  const [billingDetailAction, setBillingDetailAction] = React.useState<'csv' | 'pdf' | null>(null);
  const [invoicesData, setInvoicesData] = React.useState<IInvoices[]>([]);
  const [lineItems, setLineItems] = React.useState([]);
  const [isReportTenant, setIsReportTenant] = React.useState<boolean>(false);
  const [isSendBill, setIsSendBill] = React.useState<boolean>(false);
  const [isPaymentReport, setIsPaymentReport] = React.useState<boolean>(false);
  const [billsSentTimestamp, setBillsSentTimestamp] = React.useState<number | null>(null);
  const { trackEvent } = useMixPanel();
  const { showLoading, hideLoading } = useModal();

  const { data: outageStatusResponse = defaultOutageStatusResponse } = useSWR(
    OUTAGE_STATUS_SWR_KEY,
    getOutageStatus,
    {
      refreshInterval: OUTAGE_STATUS_REFRESH_MILLIS,
    },
  );

  const generateBillForSelectedId = (selectedBillId, info, action?: 'csv' | 'pdf') => {
    if (selectedBillId == null) return;
    showLoading('Generating Tenant Usage & Billing Report', 'This will only take a moment.');

    setLoading(true);
    trackEvent('Bill Generated', info);
    generateBill(selectedBillId, mapOutageStatusToIsElasticsearchOutage(outageStatusResponse))
      .then((resp) => {
        setInvoicesData(info.invoices);
        setLineItems(info.lineItems);
        setBillsSentTimestamp(info.billsSentTimestamp);
        if (action) setBillingDetailAction(action);
        setSelectedBillingDetail(resp);
      })
      .finally(() => {
        setLoading(false);
        hideLoading();
      });
  };

  const classes = useStyles();

  const rows: RowData[] = billsToRows(bills, dueDateDays ?? 21, stripeConnect, timeZone);

  const handleRequestSort = (event: React.MouseEvent, property: SortableProperty) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) =>
    setPage(newPage);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<{ value: unknown }>) => {
    setRowsPerPage(parseInt(event.target.value as string, 10));
    setPage(0);
  };

  const handleClickDelete = (rowId, rowInfo) => {
    setSelectedRowId(rowId);
    setSelectedRow(rowInfo);
    setOpenConfirmModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedRowId && selectedRow) {
      setLoading(true);
      await remove(selectedRowId);
      await trackEvent('Bill Deleted', {
        StartDate: convertTimestampToTimeZone(selectedRow.start, timeZone),
        EndDate: convertTimestampToTimeZone(selectedRow.end, timeZone),
        WaterUsage: selectedRow.volume,
        VolumeUnit: selectedRow.volumeUnits || 'GALLONS_1K',
        Cost: selectedRow.cost,
        RateMethod: selectedRow.rateMethod,
        PropertyName: structureName,
        PropertyID: structureId,
        BillID: selectedRowId,
        EffectiveRate: selectedRow.effectiveRate,
      });
      setLoading(false);
      setSelectedRowId(null);
    }

    setOpenConfirmModal(false);
    setLoading(false);
  };

  const handleCloseDeleteModal = () => {
    setOpenConfirmModal(false);
    setSelectedRowId(null);
  };

  const handleAddNewBill = async (data: FormValues) => {
    const start = (data.startDate ? data.startDate : 0) / 1000;
    const end = (data.endDate ? data.endDate : 0) / 1000;
    setLoading(true);

    const billRes = await create({
      cost: +data.cost!,
      volume: +data.volume!,
      start,
      end,
      structureId,
      volume_units: data.unit || 'GALLONS_1K',
      rate_method: data.rateMethod,
      effective_rate: data.effectiveRate,
      fees: data.fees,
      eUsage: data.eUsage,
    });

    await trackEvent('Bill Created', {
      StartDate: convertTimestampToTimeZone(start, timeZone),
      EndDate: convertTimestampToTimeZone(end, timeZone),
      WaterUsage: +data.volume!,
      VolumeUnit: data.unit || 'GALLONS_1K',
      Cost: +data.cost!,
      RateMethod: data.rateMethod,
      PropertyName: structureName,
      PropertyID: structureId,
      BillID: billRes.id,
    });

    setLoading(false);
    setIsSendBill(true);
    generateBillForSelectedId(billRes.id, {
      StartDate: convertTimestampToTimeZone(start, timeZone),
      EndDate: convertTimestampToTimeZone(end, timeZone),
      WaterUsage: +data.volume!,
      VolumeUnit: data.unit || 'GALLONS_1K',
      Cost: +data.cost!,
      RateMethod: data.rateMethod,
      PropertyName: structureName,
      PropertyID: structureId,
      BillID: billRes.id,
      EffectiveRate: data.effectiveRate,
      invoices: [],
      lineItems: billRes.line_items,
      billsSentTimestamp: null,
    });
    setIsAddBillOpen(false);
  };

  const handleEditBill = async (data: FormValues) => {
    const start = (data.startDate ? data.startDate : 0) / 1000;
    const end = (data.endDate ? data.endDate : 0) / 1000;
    setLoading(true);

    await edit({
      id: isEditingBill,
      cost: Number(data.cost),
      volume: Number(data.volume),
      volume_units: data.unit,
      start,
      end,
      structureId,
      rate_method: data.rateMethod,
      effective_rate: data.effectiveRate,
    });

    await trackEvent('Bill Edited', {
      StartDate: convertTimestampToTimeZone(start, timeZone),
      EndDate: convertTimestampToTimeZone(end, timeZone),
      WaterUsage: +data.volume!,
      VolumeUnit: data.unit || 'GALLONS_1K',
      Cost: +data.cost!,
      RateMethod: data.rateMethod,
      PropertyName: structureName,
      PropertyID: structureId,
      BillID: isEditingBill,
      EffectiveRate: data.effectiveRate,
    });
    setIsSendBill(true);
    // Find the row being edited to get its invoices/lineItems
    const editedRow = rows.find((r) => r.id === isEditingBill);
    generateBillForSelectedId(isEditingBill, {
      StartDate: convertTimestampToTimeZone(start, timeZone),
      EndDate: convertTimestampToTimeZone(end, timeZone),
      WaterUsage: +data.volume!,
      VolumeUnit: data.unit || 'GALLONS_1K',
      Cost: +data.cost!,
      RateMethod: data.rateMethod,
      PropertyName: structureName,
      PropertyID: structureId,
      BillID: isEditingBill,
      EffectiveRate: data.effectiveRate,
      invoices: editedRow?.invoices || [],
      lineItems: editedRow?.lineItems || [],
      billsSentTimestamp: editedRow?.billsSentTimestamp || null,
    });
    setIsEditingBill(null);
    setLoading(false);
  };

  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const [menuSelectedId, setMenuSelectedId] = React.useState(null);

  const handleMenuClick = (event, id) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuSelectedId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuSelectedId(null);
  };

  const UNIT_LABELS: Record<'GALLONS_1K' | 'CCF', string> = {
    GALLONS_1K: '1000 Gallons',
    CCF: 'CCF (Centum Cubic Feet)',
  };

  const isEmpty = rows.length === 0;
  const colCount = stripeConnect ? 7 : 6; // however many <TableCell> in your header

  return (
    <ErrorBoundary>
      <div className={classes.root}>
        <Paper
          variant="outlined"
          sx={{ borderRadius: 2, borderColor: 'divider', borderWidth: 2, mt: -2 }}
          className={classes.paper}
        >
          <EnhancedTableToolbar onAdd={() => setIsAddBillOpen(true)} />
          <Divider sx={{ borderColor: 'grey', width: '100%', mt: 1 }} />
          <TableContainer>
            <Table className={classes.table} size="medium">
              <EnhancedTableHead
                classes={classes}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                stripeConnect={stripeConnect}
              />
              <TableBody sx={{ position: 'relative' }}>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={colCount + 1} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : isEmpty ? (
                  <TableRow>
                    <TableCell
                      colSpan={colCount + 1} // +1 for hamburger menu column
                      sx={{ py: 10 }}
                      align="center"
                    >
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        gap={3} // spacing between items
                      >
                        <Typography color="text.secondary">No bills found.</Typography>
                        <SWLoadingButton onClick={() => setIsAddBillOpen(true)}>
                          Add your first Bill
                        </SWLoadingButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  stableSort(rows, getComparator(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => {
                      // start with the water‐usage cost
                      let totalCost = row.cost || 0;

                      if (row.lineItems && row.lineItems.length !== 0) {
                        const splitItems = row.lineItems.filter((item) => item.type === 'SPLIT');
                        const flatItems = row.lineItems.filter((item) => item.type === 'FLAT');
                        // Only count billable units (exclude NON_BILLABLE and COMMON_AREA)
                        const billableUnitIds = new Set(
                          units
                            .filter((u) => {
                              const flags: string[] = Array.isArray(u.unit_flags)
                                ? u.unit_flags
                                : [u.unit_flags ?? ''];
                              return (
                                !flags.includes('NON_BILLABLE') && !flags.includes('COMMON_AREA')
                              );
                            })
                            .map((u) => u.id),
                        );
                        const billableUnitsCount = billableUnitIds.size;

                        // Filter UNIT_FLAT fees to only include those for billable units
                        const unitFlatItems = row.lineItems.filter(
                          (item) =>
                            item.type === 'UNIT_FLAT' &&
                            item.unitId &&
                            billableUnitIds.has(item.unitId),
                        );

                        // add split‐fee totals
                        splitItems.forEach((item) => {
                          totalCost += item.cost;
                        });
                        // add per-unit flat fees (only for billable units)
                        flatItems.forEach((item) => {
                          totalCost += item.cost * billableUnitsCount;
                        });
                        // add unit-flat fees (only for billable units, filtered above)
                        unitFlatItems.forEach((item) => {
                          totalCost += item.cost;
                        });
                      }

                      return (
                        <TableRow hover key={row.name}>
                          {/* start date */}
                          <TableCell align="center">
                            {convertTimestampToTimeZone(row.start, timeZone, true)}
                          </TableCell>
                          {/* end date */}
                          <TableCell align="center">
                            {convertTimestampToTimeZone(row.end, timeZone, true)}
                          </TableCell>
                          {/* water usage */}
                          <TableCell align="center">{formatNumber(row.volume, 3)}</TableCell>
                          {/* volume unit */}
                          <TableCell align="center">
                            {UNIT_LABELS[row.volumeUnits as 'GALLONS_1K' | 'CCF']}
                          </TableCell>
                          {/* TODO: display the effective rate after backend supports providing it */}
                          {/* <TableCell align="center">{`${row.invoices[0].} hi`}</TableCell> */}
                          {/* amount due */}
                          <TableCell align="center">{`$${formatNumber(totalCost, 2)}`}</TableCell>
                          {/* due date */}
                          <TableCell align="center">{formatDueDate(row.due, timeZone)}</TableCell>
                          {/* status */}
                          <TableCell align="center">
                            {stripeConnect ? (
                              // Stripe: show payment progress
                              row.billsSentTimestamp && (
                                <Stack>
                                  <RenderStatus invoices={row.invoices} />
                                </Stack>
                              )
                            ) : (
                              // Non-Stripe: show invoice generation status
                              <RenderInvoiceGenerationStatus invoices={row.invoices} />
                            )}
                          </TableCell>
                          {/* hamburger menu */}
                          <TableCell align="center">
                            <IconButton
                              aria-controls={`menu-${row.id}`}
                              aria-haspopup="true"
                              onClick={(event) => handleMenuClick(event, row.id)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                            <Menu
                              id={`menu-${row.id}`}
                              anchorEl={menuAnchorEl}
                              open={Boolean(menuAnchorEl) && menuSelectedId === row.id}
                              onClose={handleMenuClose}
                            >
                              <MenuItem
                                onClick={() => {
                                  // Refresh billing data to get latest invoice status
                                  mutate(bills, true);
                                  generateBillForSelectedId(row.id, {
                                    StartDate: convertTimestampToTimeZone(row.start, timeZone),
                                    EndDate: convertTimestampToTimeZone(row.end, timeZone),
                                    WaterUsage: row.volume,
                                    VolumeUnit: row.volumeUnits || 'GALLONS_1K',
                                    Cost: row.cost,
                                    RateMethod: row.rateMethod,
                                    PropertyName: structureName,
                                    PropertyID: structureId,
                                    BillID: isEditingBill,
                                    EffectiveRate: row.effectiveRate,
                                    invoices: row.invoices,
                                    lineItems: row.lineItems,
                                    billsSentTimestamp: row.billsSentTimestamp,
                                  });
                                  setIsReportTenant(false); // clear the "usage report" flag
                                  setIsSendBill(true); // turn on the "send bill" flag
                                  handleMenuClose();
                                }}
                              >
                                View Bill
                              </MenuItem>
                              {stripeConnect && (
                                <MenuItem
                                  onClick={() => {
                                    let a = document.createElement('a');
                                    a.target = '_blank';
                                    a.href = 'https://dashboard.stripe.com/invoices/';
                                    a.click();
                                    handleMenuClose();
                                  }}
                                >
                                  View Invoices
                                </MenuItem>
                              )}
                              {!stripeConnect && (
                                <MenuItem
                                  onClick={() => {
                                    setIsEditingBill(row.id);
                                    handleMenuClose();
                                  }}
                                >
                                  Edit Bill
                                </MenuItem>
                              )}
                              <MenuItem
                                onClick={() => {
                                  generateBillForSelectedId(row.id, {
                                    StartDate: convertTimestampToTimeZone(row.start, timeZone),
                                    EndDate: convertTimestampToTimeZone(row.end, timeZone),
                                    WaterUsage: row.volume,
                                    VolumeUnit: row.volumeUnits || 'GALLONS_1K',
                                    Cost: row.cost,
                                    RateMethod: row.rateMethod,
                                    PropertyName: structureName,
                                    PropertyID: structureId,
                                    BillID: isEditingBill,
                                    EffectiveRate: row.effectiveRate,
                                    invoices: row.invoices,
                                    lineItems: row.lineItems,
                                    billsSentTimestamp: row.billsSentTimestamp,
                                  });
                                  setIsReportTenant(true);
                                  handleMenuClose();
                                }}
                              >
                                Send Usage to Tenants
                              </MenuItem>
                              <MenuItem
                                onClick={() => {
                                  handleClickDelete(row.id, row);
                                  handleMenuClose();
                                }}
                              >
                                Delete Utility Bill
                              </MenuItem>
                            </Menu>
                          </TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {!isEmpty && (
            <TablePagination
              rowsPerPageOptions={
                rows.length < 5
                  ? [rows.length]
                  : rows.length === 5
                  ? [5]
                  : rows.length < 10 && rows.length > 5
                  ? [5, rows.length]
                  : rows.length === 10
                  ? [5, 10]
                  : rows.length < 20 && rows.length > 10
                  ? [5, 10, rows.length]
                  : [5, 10, 20]
              }
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onRowsPerPageChange={handleChangeRowsPerPage}
              component={'div'}
              onPageChange={handleChangePage}
            />
          )}
        </Paper>
      </div>

      <BillForm
        type="ADD"
        units={units}
        volumeUnits={volumeUnits}
        open={isAddBillOpen}
        onClose={() => setIsAddBillOpen(false)}
        onSubmit={handleAddNewBill}
        rateMethod={defaultRateMethod}
        loading={loading}
        propertyTimeZone={timeZone}
        stripeConnect={stripeConnect}
        dueDateDays={dueDateDays}
      />

      {/* Edit Utility Bill */}
      <BillForm
        type={'EDIT'}
        units={units}
        volumeUnits={volumeUnits}
        open={Boolean(isEditingBill)}
        onClose={() => setIsEditingBill(null)}
        cost={rows.find((r) => r.id === isEditingBill)?.cost}
        volume={rows.find((r) => r.id === isEditingBill)?.volume}
        start={(rows.find((r) => r.id === isEditingBill)?.start || 0) * 1000}
        end={(rows.find((r) => r.id === isEditingBill)?.end || 0) * 1000}
        rateMethod={rows.find((r) => r.id === isEditingBill)?.rateMethod || defaultRateMethod}
        onSubmit={handleEditBill}
        loading={loading}
        propertyTimeZone={timeZone}
        stripeConnect={stripeConnect}
        dueDateDays={dueDateDays}
      />

      {selectedBillingDetail && (
        <BillBreakdown
          open={!!selectedBillingDetail}
          onClose={() => {
            setSelectedBillingDetail(null);
            setBillingDetailAction(null);
            setIsReportTenant(false);
            setIsSendBill(false);
            setIsPaymentReport(false);
          }}
          structureName={structureName}
          bill={selectedBillingDetail}
          autoDownload={billingDetailAction}
          needReportButton={isReportTenant}
          needSendBillButton={isSendBill}
          structureAddress={structureAddress}
          timeZone={timeZone}
          stripeConnect={stripeConnect}
          invoices={invoicesData}
          isPaymentReport={isPaymentReport}
          lineItems={lineItems}
          billsSentTimestamp={billsSentTimestamp}
          payOnlineUrl={payOnlineUrl}
          paymentInstructions={paymentInstructions}
          dueDateDays={dueDateDays}
          onPaymentSettingsUpdate={onPaymentSettingsUpdate}
          onRefreshBills={async () => {
            const updatedBills = await mutate(undefined as any);
            // Find the current bill and update invoicesData
            const currentBill = updatedBills?.find(
              (b: any) => b.id === selectedBillingDetail?.billing_id,
            );
            if (currentBill?.invoices) {
              setInvoicesData(currentBill.invoices);
            }
          }}
        />
      )}

      <DeleteConfirmDialog
        open={openConfirmModal}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteConfirm}
        title="Do you want to remove this bill?"
        loading={loading}
        sub={selectedRow?.billsSentTimestamp ? 'This bill has been sent to tenants.' : ''}
      />
    </ErrorBoundary>
  );
};

const RenderStatus = ({ invoices }: { invoices: IInvoices[] }) => {
  const paidInvoices = invoices.filter((invoice) => invoice.stripeLastStatus === 'paid');

  const percentage = invoices.length > 0 ? (paidInvoices.length / invoices.length) * 100 : 100;
  return (
    <LinearProgressWithLabel
      color="secondary"
      value={percentage}
      label={`${paidInvoices.length} of ${invoices.length}`}
    />
  );
};

const RenderInvoiceGenerationStatus = ({ invoices }: { invoices: IInvoices[] }) => {
  if (invoices.length === 0) {
    // No invoices yet
    return <RemoveIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />;
  }

  const generatedCount = invoices.filter((inv) => inv.customInvoicePdfUrl).length;

  if (generatedCount === 0) {
    // None generated
    return <RemoveIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />;
  }

  if (generatedCount === invoices.length) {
    // All generated
    return <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />;
  }

  // Partial - some generated
  return <ContrastIcon sx={{ color: '#ff9800', fontSize: 20 }} />;
};
